import { useMemo, useState } from 'react';
import { createFileRoute, Outlet, useRouter, useRouterState } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Copy, Mail, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/atoms/button';
import { Input } from '@/components/atoms/input';
import { Checkbox } from '@/components/atoms/checkbox';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/atoms/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/atoms/form';
import { EntityIndexLayout, WorkspaceShell, useWorkspaceHeader } from '@/components/layouts';
import { UserListPanel } from '@/components/organisms/settings-users/UserListPanel';
import { UserRowActions, UserTableView } from '@/components/organisms/settings-users/UserTableView';
import { displayName } from '@/components/organisms/settings-users/user-display';
import { buildInviteUrl } from '@/components/organisms/settings-users/invite-url';
import { useRoles } from '@/hooks/settings/useRoles';
import { useInviteUser } from '@/hooks/settings/useInviteUser';
import { useDeleteUser } from '@/hooks/settings/useDeleteUser';
import { useResendInvitation } from '@/hooks/settings/useResendInvitation';
import { useCancelInvitation } from '@/hooks/settings/useCancelInvitation';
import { useHasPermission } from '@/hooks/usePermissions';
import type { RoleListItem, TeamUser } from '@/types/settings';

export const Route = createFileRoute('/_dashboard/dashboard/settings/users')({
  component: UsersSettingsPage,
});

// Re-exported (via the value import above) so existing consumers
// (e.g. users.test.tsx) keep importing `UserRowActions` from this route
// module while the implementation lives alongside the extracted table view.
export { UserRowActions };

// Manual pathname derivation mirrors the membership-plans mold (which uses
// regex/string matching rather than useEntityRouteState).
const USER_DETAIL_ROUTE_PATTERN = /^\/dashboard\/settings\/users\/([^/]+)\/?$/;
const USER_EDIT_ROUTE_PATTERN = /^\/dashboard\/settings\/users\/([^/]+)\/edit\/?$/;
const USER_NEW_ROUTE_PATTERN = /^\/dashboard\/settings\/users\/new\/?$/;
const USER_INDEX_ROUTE_PATTERN = /^\/dashboard\/settings\/users\/?$/;

// ─── Schemas ────────────────────────────────────────────────────────────────

const inviteUserSchema = z.object({
  email: z.string().email('Enter a valid email'),
});

type InviteUserForm = z.infer<typeof inviteUserSchema>;

// ─── Component ──────────────────────────────────────────────────────────────

function UsersSettingsPage() {
  const router = useRouter();
  const { location } = useRouterState();
  const { data: rolesData } = useRoles();
  const roles = useMemo(() => rolesData?.data ?? [], [rolesData?.data]);
  const inviteUser = useInviteUser();
  const deleteUser = useDeleteUser();
  const resendInvitation = useResendInvitation();
  const cancelInvitation = useCancelInvitation();
  const canCreateUsers = useHasPermission('users:create');
  const canEditUsers = useHasPermission('users:edit');
  const canDeleteUsers = useHasPermission('users:delete');

  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteLink, setInviteLink] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<TeamUser | null>(null);
  const [resendTarget, setResendTarget] = useState<TeamUser | null>(null);
  const [cancelTarget, setCancelTarget] = useState<TeamUser | null>(null);

  const detailRouteUserId = location.pathname.match(USER_DETAIL_ROUTE_PATTERN)?.[1];
  const detailUserId = detailRouteUserId && detailRouteUserId !== 'new' ? detailRouteUserId : null;
  const editUserId = location.pathname.match(USER_EDIT_ROUTE_PATTERN)?.[1];
  const selectedId = editUserId ?? detailUserId ?? undefined;
  const isNewRoute = USER_NEW_ROUTE_PATTERN.test(location.pathname);
  const isEditRoute = Boolean(editUserId);
  const isFormMode = isNewRoute || isEditRoute;
  const hasChildRoute = Boolean(selectedId) || isNewRoute;
  const isIndexRoute = USER_INDEX_ROUTE_PATTERN.test(location.pathname);

  function openDetail(userId: string) {
    void router.navigate({ to: '/dashboard/settings/users/$id', params: { id: userId } });
  }

  // Auto-select (split desktop) replaces history so Back doesn't loop /users → /$id.
  function autoSelectDetail(userId: string) {
    void router.navigate({
      to: '/dashboard/settings/users/$id',
      params: { id: userId },
      replace: true,
    });
  }

  function handleInviteOpenChange(open: boolean) {
    if (open && !canCreateUsers) return;
    setInviteOpen(open);
    if (!open) setInviteLink(null);
  }

  function handleCopyInviteLink() {
    if (!inviteLink) return;
    void navigator.clipboard.writeText(inviteLink).then(
      () => toast.success('Invite link copied'),
      () => toast.error('Could not copy invite link'),
    );
  }

  return (
    <WorkspaceShell hideHeader={isFormMode}>
      <UsersHeaderSlot
        hidden={isFormMode}
        showTitleFallback={!isFormMode && !selectedId}
        canCreateUsers={canCreateUsers}
        onNewUser={() => {
          if (!canCreateUsers) return;
          void router.navigate({ to: '/dashboard/settings/users/new' });
        }}
        onInvite={() => setInviteOpen(true)}
      />

      <EntityIndexLayout
        title="Users"
        storageKey="users-view"
        hasChildRoute={hasChildRoute}
        onResetToIndex={() => void router.navigate({ to: '/dashboard/settings/users' })}
        viewToggleShowLabels
        viewToggleActiveTone="muted"
        splitWidthClass="w-[360px]"
        tableSheetWidthClass="w-[620px] sm:max-w-[620px]"
        hideListInSplit={isFormMode}
        fullPageFormInTable
        splitList={() => (
          <div className="flex h-full flex-col">
            <UserListPanel
              selectedId={selectedId}
              onSelect={openDetail}
              onAutoSelect={autoSelectDetail}
              isIndexRoute={isIndexRoute}
            />
          </div>
        )}
        splitDetail={<Outlet />}
        tableView={({ openInSheet }) => (
          <UserTableView
            canEdit={canEditUsers}
            canDelete={canDeleteUsers}
            canManageInvitations={canCreateUsers}
            onView={(userId) => openInSheet(() => openDetail(userId))}
            onEdit={(userId) =>
              canEditUsers &&
              void router.navigate({
                to: '/dashboard/settings/users/$id/edit',
                params: { id: userId },
              })
            }
            onDelete={(user) => setDeleteTarget(user)}
            onResendInvitation={(user) => setResendTarget(user)}
            onCancelInvitation={(user) => setCancelTarget(user)}
          />
        )}
        sheetContent={<Outlet />}
        mobileContent={
          <div className="flex h-full flex-col md:hidden">
            {isIndexRoute ? (
              <UserListPanel
                selectedId={selectedId}
                onSelect={openDetail}
                onAutoSelect={autoSelectDetail}
                isIndexRoute={isIndexRoute}
              />
            ) : (
              <Outlet />
            )}
          </div>
        }
      />

      {/* Invite User Dialog */}
      <InviteUserDialog
        open={canCreateUsers && inviteOpen}
        onOpenChange={handleInviteOpenChange}
        roles={roles}
        inviteLink={inviteLink}
        onCopyInviteLink={handleCopyInviteLink}
        onInvite={(email, roleIds) => {
          inviteUser.mutate(
            { email, roleIds: roleIds.length > 0 ? roleIds : undefined },
            {
              onSuccess: (data) => {
                setInviteLink(buildInviteUrl(data.claimToken));
              },
            },
          );
        }}
        isPending={inviteUser.isPending}
      />

      <Dialog
        open={canDeleteUsers && Boolean(deleteTarget)}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete user?</DialogTitle>
            <p className="text-sm text-muted-foreground">
              {deleteTarget
                ? `Delete ${displayName(deleteTarget)}? This removes their company access.`
                : 'This removes company access for this user.'}
            </p>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              disabled={deleteUser.isPending}
              onClick={() => setDeleteTarget(null)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              disabled={deleteUser.isPending}
              onClick={() => {
                if (!deleteTarget) return;
                deleteUser.mutate(deleteTarget.id, {
                  onSuccess: () => setDeleteTarget(null),
                });
              }}
            >
              {deleteUser.isPending ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel Invitation confirmation. Same `users:create` gate as
          resend — the backend endpoint reuses that permission. Confirming
          invalidates the previously-sent link AND removes the invited
          user, so the body says so plainly — and names the two ways back:
          Edit sets a password without cancelling, and adding the same
          email later restores the account. */}
      <Dialog
        open={canCreateUsers && Boolean(cancelTarget)}
        onOpenChange={(open) => {
          if (!open) setCancelTarget(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cancel invitation?</DialogTitle>
            <p className="text-sm text-muted-foreground">
              This invalidates the link and removes{' '}
              {cancelTarget ? displayName(cancelTarget) : 'the user'} from this list. Adding the
              same email again later brings their account back. To give them a password instead,
              keep the invitation and set one from Edit.
            </p>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              disabled={cancelInvitation.isPending}
              onClick={() => setCancelTarget(null)}
            >
              Keep invitation
            </Button>
            <Button
              type="button"
              variant="destructive"
              disabled={cancelInvitation.isPending}
              onClick={() => {
                if (!cancelTarget) return;
                cancelInvitation.mutate(cancelTarget.id, {
                  onSuccess: () => setCancelTarget(null),
                });
              }}
            >
              {cancelInvitation.isPending ? 'Cancelling...' : 'Cancel invitation'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Resend Invitation confirmation. Gated by canCreateUsers because the
          backend endpoint reuses the `users:create` permission — keeping the
          same permission boundary avoids a hidden authorization split. */}
      <Dialog
        open={canCreateUsers && Boolean(resendTarget)}
        onOpenChange={(open) => {
          if (!open) setResendTarget(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Resend invitation?</DialogTitle>
            <p className="text-sm text-muted-foreground">
              {resendTarget
                ? `Resend invitation email to ${resendTarget.email}? Any previous invitation link will stop working.`
                : 'Resend the invitation email for this user.'}
            </p>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              disabled={resendInvitation.isPending}
              onClick={() => setResendTarget(null)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              disabled={resendInvitation.isPending}
              onClick={() => {
                if (!resendTarget) return;
                resendInvitation.mutate(resendTarget.id, {
                  onSuccess: () => setResendTarget(null),
                });
              }}
            >
              {resendInvitation.isPending ? 'Sending...' : 'Resend'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </WorkspaceShell>
  );
}

function UsersHeaderSlot({
  hidden,
  showTitleFallback,
  canCreateUsers,
  onNewUser,
  onInvite,
}: {
  hidden: boolean;
  showTitleFallback: boolean;
  canCreateUsers: boolean;
  onNewUser: () => void;
  onInvite: () => void;
}) {
  const newButton = useMemo(() => {
    if (hidden || !canCreateUsers) return null;
    return (
      <Button size="sm" title="Add user" onClick={onNewUser}>
        <Plus size={14} />
        Add user
      </Button>
    );
  }, [canCreateUsers, hidden, onNewUser]);

  const toolbarActions = useMemo(() => {
    if (hidden || !canCreateUsers) return null;
    return (
      <Button
        variant="outline"
        size="sm"
        title="Invite"
        aria-label="Invite user"
        onClick={onInvite}
      >
        <Mail size={14} />
        Invite
      </Button>
    );
  }, [canCreateUsers, hidden, onInvite]);

  // Fallback franja title when no user is selected (table view, or the brief
  // index moment). When a user is open the detail route's own slot supplies
  // the richer context, taking over this key.
  const contextHeader = useMemo(() => {
    if (!showTitleFallback) return null;
    return <h1 className="truncate text-base font-semibold">Users</h1>;
  }, [showTitleFallback]);

  useWorkspaceHeader({ newButton, toolbarActions, contextHeader }, [
    newButton,
    toolbarActions,
    contextHeader,
  ]);
  return null;
}

// ─── Invite User Dialog ─────────────────────────────────────────────────────

function InviteUserDialog({
  open,
  onOpenChange,
  roles,
  inviteLink,
  onCopyInviteLink,
  onInvite,
  isPending,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  roles: RoleListItem[];
  inviteLink: string | null;
  onCopyInviteLink: () => void;
  onInvite: (email: string, roleIds: string[]) => void;
  isPending: boolean;
}) {
  const form = useForm<InviteUserForm>({
    resolver: zodResolver(inviteUserSchema),
    defaultValues: { email: '' },
  });

  const [selectedRoleIds, setSelectedRoleIds] = useState<Set<string>>(new Set());

  function toggleRole(roleId: string) {
    setSelectedRoleIds((prev) => {
      const next = new Set(prev);
      if (next.has(roleId)) next.delete(roleId);
      else next.add(roleId);
      return next;
    });
  }

  function handleSubmit(values: InviteUserForm) {
    onInvite(values.email, [...selectedRoleIds]);
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) {
          form.reset();
          setSelectedRoleIds(new Set());
        }
        onOpenChange(o);
      }}
    >
      <DialogContent className="flex max-h-[85vh] flex-col overflow-hidden p-0 sm:max-w-md">
        <DialogHeader className="shrink-0 border-b px-6 py-5">
          <DialogTitle>Invite User</DialogTitle>
          <p className="text-sm text-muted-foreground">
            Create an invite link. They'll set up their own password when they accept.
          </p>
        </DialogHeader>

        {inviteLink ? (
          <>
            <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">
              <div className="rounded-md border bg-muted/30 p-3">
                <p className="text-sm font-medium">Invite link</p>
                <div className="mt-2 flex gap-2">
                  <Input
                    value={inviteLink}
                    readOnly
                    className="font-mono text-xs"
                    onFocus={(event) => event.currentTarget.select()}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    aria-label="Copy invite link"
                    onClick={onCopyInviteLink}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
            <DialogFooter className="shrink-0 border-t px-6 py-4">
              <Button type="button" onClick={() => onOpenChange(false)}>
                Done
              </Button>
            </DialogFooter>
          </>
        ) : (
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(handleSubmit)}
              className="flex min-h-0 flex-1 flex-col"
            >
              <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-6 py-5">
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email Address</FormLabel>
                      <FormControl>
                        <Input type="email" placeholder="jane@example.com" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div>
                  <p className="mb-2 text-sm font-medium">Roles (assigned on accept)</p>
                  <div className="space-y-2">
                    {roles.map((role) => (
                      <label
                        key={role.id}
                        className="flex min-h-16 cursor-pointer items-start gap-3 rounded-md border border-border p-3 hover:bg-muted/50"
                      >
                        <Checkbox
                          checked={selectedRoleIds.has(role.id)}
                          onCheckedChange={() => toggleRole(role.id)}
                          className="mt-1"
                        />
                        <div className="min-w-0 space-y-0.5">
                          <p className="text-sm font-medium">{role.name}</p>
                          <p className="text-xs leading-5 text-muted-foreground">
                            {role.description || 'No description.'}
                          </p>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              <DialogFooter className="shrink-0 border-t px-6 py-4">
                <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={isPending}>
                  {isPending ? 'Creating...' : 'Create Link'}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        )}
      </DialogContent>
    </Dialog>
  );
}
